/**
 * Tracking de FOTOS (Cloudinary) — impresiones y aperturas por public_id.
 *
 * "Quién vio cada foto": cada evento lleva distinct_id PostHog + geo del Worker
 * (worker_country / province / city) + country_code. Admin:
 * /admin/fotos-analytics → /api/admin/photo-analytics.
 *
 * Eventos:
 *   photo_impression — ≥50% visible ≥800ms (1× por foto por pageload)
 *   photo_open       — lightbox / vista completa (incluye swipe next/prev)
 */

import posthog from "posthog-js";
import { recordGalleryCloudinaryView } from "@/lib/galleryEngagementSignal";
import { isPostHogConfigured } from "@/lib/posthogAnalytics";
import {
  getServerTemplateConfig,
  getWorkerGeoAnalyticsProps,
} from "@/lib/serverTemplateDetection";

/** res.cloudinary.com/<cloud>/image/upload/<transforms>/v123/<public_id>.<ext> → public_id */
export function cloudinaryPublicIdFromUrl(
  url: string | null | undefined,
): string | null {
  if (!url) return null;
  try {
    const u = new URL(
      url,
      typeof window !== "undefined" ? window.location.origin : "https://x.invalid",
    );
    if (!u.hostname.includes("res.cloudinary.com")) return null;
    const parts = u.pathname.split("/").filter(Boolean);
    const uploadIdx = parts.indexOf("upload");
    if (uploadIdx < 0) return null;
    let rest = parts.slice(uploadIdx + 1);
    while (rest.length > 1 && /^([a-z]+_[^/]+)(,|$)/.test(rest[0])) {
      rest = rest.slice(1);
    }
    if (rest.length > 1 && /^v\d+$/.test(rest[0])) rest = rest.slice(1);
    if (rest.length === 0) return null;
    const joined = rest.join("/");
    return joined.replace(/\.[a-zA-Z0-9]+$/, "");
  } catch {
    return null;
  }
}

export interface PhotoTrackContext {
  section: string;
  templateType?: string;
  countryCode?: string;
  /** grid click | lightbox_swipe | portfolio | media_kit */
  openSource?:
    | "grid"
    | "lightbox_swipe"
    | "portfolio"
    | "media_kit"
    | "unknown";
  /** Override when URL is transform-heavy or not parseable (e.g. Media Kit publicId). */
  publicId?: string;
  /** Extra props (kit_slug, image_role, …) */
  extra?: Record<string, string | number | boolean | null | undefined>;
}

function resolveCountry(ctx: PhotoTrackContext): string | undefined {
  if (ctx.countryCode && ctx.countryCode !== "UNKNOWN") return ctx.countryCode;
  try {
    const cfg = getServerTemplateConfig();
    if (cfg?.country && cfg.country !== "UNKNOWN") return cfg.country;
  } catch {
    /* */
  }
  return undefined;
}

function basePhotoProps(
  src: string,
  position: number | undefined,
  ctx: PhotoTrackContext,
) {
  const fromUrl = cloudinaryPublicIdFromUrl(src);
  const publicId =
    (ctx.publicId && ctx.publicId.trim()) || fromUrl || null;
  const country = resolveCountry(ctx);
  let geo: Record<string, string | undefined> = {};
  try {
    geo = getWorkerGeoAnalyticsProps();
  } catch {
    geo = {};
  }
  // Explicit event props (admin HogQL uses worker_* + country_code; super-props are backup)
  return {
    photo_public_id: publicId ?? src.slice(0, 200),
    photo_is_cloudinary: publicId != null || Boolean(fromUrl),
    photo_src: src.slice(0, 300),
    photo_position: position,
    photo_section: ctx.section,
    photo_open_source: ctx.openSource ?? "unknown",
    template_type: ctx.templateType,
    country_code: country ?? geo.worker_country ?? undefined,
    worker_country: geo.worker_country ?? country,
    worker_province: geo.worker_province,
    worker_city: geo.worker_city,
    worker_neighborhood: geo.worker_neighborhood,
    geo_summary: geo.geo_summary,
    page_path:
      typeof window !== "undefined" ? window.location.pathname : undefined,
    site: "delfuocampo",
    ...(ctx.extra || {}),
  };
}

export function trackPhotoImpression(
  src: string,
  position: number | undefined,
  ctx: PhotoTrackContext,
): void {
  if (!isPostHogConfigured()) return;
  posthog.capture("photo_impression", basePhotoProps(src, position, ctx));
}

export function trackPhotoOpen(
  src: string,
  position: number | undefined,
  ctx: PhotoTrackContext,
): void {
  recordGalleryCloudinaryView(src);
  if (!isPostHogConfigured()) return;
  posthog.capture("photo_open", basePhotoProps(src, position, {
    ...ctx,
    openSource: ctx.openSource ?? "grid",
  }));
}
